import type { Unit } from "convert-units"
import type { ValuesType } from "utility-types"

import * as Geo from "@/base/geo"

import convert from "convert-units"
import React from "react"

import type { AnySensorKey, ReelRunGraphKey } from "./sensors"

import { REEL_RUN_GRAPH_KEYS, RUN_OBSERVATION_KEYS } from "./sensors"
import { makeValidator } from "./type-guards"
import { useRootSelector } from "@/redux/useRootSelector"

export const MeasurementSystem = {
  METRIC: "metric",
  US: "us",
} as const
export type MeasurementPreference = ValuesType<typeof MeasurementSystem>
export interface MeasurementPreferenceProps {
  measurementPreference: MeasurementPreference
}
export const isValidMeasurementPreference = makeValidator(
  Object.values(MeasurementSystem),
)
export const DEVICE_DATA_CONVERSION_KEYS = [
  ...REEL_RUN_GRAPH_KEYS,
  ...RUN_OBSERVATION_KEYS,
  "internalSoc",
  "altitude",
  "averageFlowRateLpm",
  "diameterMm",
  "hoseDiameterMm",
  "hoseWheelMmPerMagnet",
  "linearSpeedMmHMax",
  "linearSpeedMmHMin",
  "measuredM",
  "milliRpmAvg",
  "milliRpmFast",
  "milliRpmSlow",
  "mlPerPulse",
  "normalThresholdLpm",
  "overThresholdLpm",
  "nozzleDiameterMm",
  "flowRateOverrideLpm",
  "outerHoseWrapRadiusMm",
  "rateLpmAvg",
  "rateLpmMax",
  "rateLpmMin",
  "readingCelsius",
  "readingKpa",
  "runDistanceMmCurrent",
  "runDistanceMmElapsed",
  "runDistanceMmMax",
  "runSpeedMmpm",
  "slope",
  "speedMillirpm",
  "speedMmpm",
  "stoppedTimeoutMs",
  "swathWidthMm",
  "normalThresholdM",
  "highThresholdM",
  "overThresholdM",
  "targetApplicationDepthMm",
  "thresholdBufferM",
  "thresholdPsiLower",
  "thresholdPsiUpper",
  "voltageMv",
  "voltageThresholdLowMv",
  "volumeLFinal",
  "volumeLInitial",
  "volumeLTotal",
  "widthMm",
  "pivotRadiusMeters",
] as const

export type ConversionTarget = "database" | "user"

export type RequiresConversion =
  | (typeof DEVICE_DATA_CONVERSION_KEYS)[number]
  | ReelRunGraphKey

export function requiresConversion(
  key: AnySensorKey | "pivotRadiusMeters",
): key is RequiresConversion {
  return Boolean(
    Object.values(DEVICE_DATA_CONVERSION_KEYS).includes(
      key as (typeof DEVICE_DATA_CONVERSION_KEYS)[number],
    ),
  )
}

// type T = "mlPerPulse" extends AnySensorKey ? true : false
// eslint-disable-next-line consistent-return
export function getSensorConversionSpec({
  fieldName,
  measurementPreference,
  target,
}: MeasurementPreferenceProps & {
  fieldName: AnySensorKey | undefined
  target: ConversionTarget
}):
  | {
      rawUnit: Unit | "millirpm" | "mm/h" | "mm/m" | "unitless"
      sigDigits: number
      userPref: Unit | "ft/hr" | "meters/hr" | "rpm" | "unitless"
    }
  | undefined {
  if (typeof fieldName === "undefined" || !requiresConversion(fieldName)) {
    return undefined
  }
  const isMetric = measurementPreference === "metric"
  switch (fieldName) {
    //  * LENGTH (config measurements etc.)
    // decimals for us users, not for metric (mm)
    case "diameterMm":
    case "outerHoseWrapRadiusMm":
    case "nozzleDiameterMm":
    case "hoseDiameterMm":
    case "hoseWheelMmPerMagnet":
    case "applicationRateReportsMm":
    case "applicationRateEstimatedMm":
    case "widthMm": {
      let sigDigits = 0
      if (!isMetric && target === "user") {
        sigDigits = 1
      }
      return { rawUnit: "mm", sigDigits, userPref: isMetric ? "mm" : "in" }
    }
    // * PRESSURE
    case "pressureObservedKpa":
    case "pressureReportsKpa":
    case "readingKpa":
    case "thresholdPsiLower":
    case "thresholdPsiUpper": {
      const userPref = isMetric ? "kPa" : "psi"
      return { rawUnit: "psi", sigDigits: 0, userPref }
    }
    case "normalThresholdM":
    case "highThresholdM":
    case "overThresholdM":
    case "thresholdBufferM":
      return { rawUnit: "m", sigDigits: 5, userPref: isMetric ? "m" : "ft" }
    case "pivotRadiusMeters": {
      return { rawUnit: "m", sigDigits: 0, userPref: isMetric ? "m" : "ft" }
    }
    // * LENGTH (field dimensions)
    case "altitude":
    case "distanceObservedMm":
    case "runDistanceMmCurrent":
    case "runDistanceMmElapsed":
    case "runDistanceMmMax":
    case "swathWidthMm": {
      return { rawUnit: "mm", sigDigits: 0, userPref: isMetric ? "m" : "ft" }
    }
    case "readingCelsius": {
      return { rawUnit: "C", sigDigits: 1, userPref: isMetric ? "C" : "F" }
    }
    case "voltageThresholdLowMv":
      return { rawUnit: "mV", sigDigits: 0, userPref: "V" }
    // Stored in ms (firmware units), displayed in minutes for both
    // measurement systems
    case "stoppedTimeoutMs":
      return { rawUnit: "ms", sigDigits: 1, userPref: "min" }
    // Flow analog calibration slope relates a raw ADC reading to L/min
    // (flow_rate_lpm = (reading - intercept) / slope) — no user-facing
    // unit and no conversion. Listed only so the input gets decimal
    // handling with 6 significant digits.
    case "slope":
      return { rawUnit: "unitless", sigDigits: 6, userPref: "unitless" }
    // rpm
    case "speedMillirpm":
    case "milliRpmFast":
    case "milliRpmSlow":
    case "milliRpmAvg":
      return { rawUnit: "millirpm", sigDigits: 2, userPref: "rpm" }
    // linear speeds
    case "linearSpeedMmHMin":
    case "linearSpeedMmHMax": {
      const userPref = isMetric ? "meters/hr" : "ft/hr"
      return { rawUnit: "mm/h", sigDigits: 1, userPref }
    }
    case "speedObservedMmpm":
    case "speedReportsMmpm":
    case "runSpeedMmpm":
    case "speedMmpm": {
      const userPref = isMetric ? "meters/hr" : "ft/hr"
      return { rawUnit: "mm/m", sigDigits: 0, userPref }
    }
    case "voltageMv": {
      return { rawUnit: "mV", sigDigits: 1, userPref: "V" }
    }
    // flow rate
    case "normalThresholdLpm":
    case "overThresholdLpm":
    case "flowRateEstimatedLpm":
    case "flowRateOverrideLpm":
    case "rateLpmMin":
    case "rateLpmMax":
    case "rateLpmAvg":
    case "averageFlowRateLpm": {
      const userPref = isMetric ? "l/min" : "gal/min"
      return { rawUnit: "l/min", sigDigits: 1, userPref }
    }
    // application depth (for irrigation)
    case "targetApplicationDepthMm": {
      // Store in mm, display as mm (metric) or inches (US)
      let sigDigits = 1
      if (!isMetric && target === "user") {
        sigDigits = 2 // More precision for inches
      }
      return { rawUnit: "mm", sigDigits, userPref: isMetric ? "mm" : "in" }
    }
    case "volumeLTotal":
    case "volumeLInitial":
    case "volumeLFinal": {
      return { rawUnit: "l", sigDigits: 1, userPref: isMetric ? "l" : "gal" }
    }
    case "internalSoc": {
      return { rawUnit: "s", sigDigits: 1, userPref: "s" }
    }
    case "mlPerPulse": {
      return {
        rawUnit: "ml",
        sigDigits: 5,
        userPref: isMetric ? "l" : "gal",
      }
    }
    case "measuredM": {
      return {
        rawUnit: "m",
        sigDigits: 2,
        userPref: isMetric ? "m" : "ft",
      }
    }
  }
}
/**
 * Takes any valid sensor key/value pair and converts the value from
 * specification (see getConversionUnits).
 *
 * @param argument
 * @param argument.fieldName - any sensor configuration or event key
 * @param argument.value - any primitive value or a geojson point
 * @param argument.isMetric - user preference
 * @param argument.target - 'database' or 'user'
 * @param argument.rawValue
 * @throws {Error} if the key has no conversion parameters specified but requires conversion
 */
export function convertSensorValue({
  fieldName,
  measurementPreference,
  rawValue,
  target,
}: MeasurementPreferenceProps & {
  fieldName: RequiresConversion
  rawValue: number
  target: ConversionTarget
}): number {
  let fromUnit: Unit
  let toUnit: Unit
  let converted: number
  const isMetric = measurementPreference === "metric"
  const conversionSpec = getSensorConversionSpec({
    fieldName,
    measurementPreference,
    target,
  })
  if (typeof conversionSpec === "undefined") {
    throw new TypeError("Conversion spec is undefined for field ${fieldName}")
  }

  const { rawUnit, sigDigits, userPref } = conversionSpec
  switch (rawUnit) {
    case "mm/m": {
      let divisor: number

      if (isMetric) {
        divisor = 16.67
      } else {
        divisor = 5.08
      }

      if (target === "database") {
        // appLogger.warn('Converting key in wrong direction', key);
      }
      converted = Math.round((rawValue / divisor) * 10) / 10
      break
    }
    case "mm/h": {
      let factor: number
      if (isMetric) {
        factor = 1000
      } else {
        factor = 304.8
      }

      if (target === "database") {
        return Geo.round(rawValue * factor)
      }
      return Geo.round(rawValue / factor, 1)
    }
    case "millirpm": {
      if (target === "database") {
        converted = Math.round(rawValue * 1000)
        break
      }
      converted = rawValue / 1000
      break
    }
    // Dimensionless fields: no conversion, only sig-digit rounding below
    case "unitless": {
      converted = rawValue
      break
    }

    default: {
      if (
        userPref === "ft/hr" ||
        userPref === "meters/hr" ||
        userPref === "rpm" ||
        userPref === "unitless"
      ) {
        throw new Error(`Fallthrough in switch: ${JSON.stringify(fieldName)}`)
      }
      if (target === "database") {
        fromUnit = userPref
        toUnit = rawUnit
      } else {
        fromUnit = rawUnit
        toUnit = userPref
      }
      converted = convert(rawValue).from(fromUnit).to(toUnit)
      break
    }
  }

  if (sigDigits > 0) {
    return Number.parseFloat(converted.toFixed(sigDigits))
  }
  return Math.round(converted)
}

export function useConvertSensorValue(
  fieldName: AnySensorKey & RequiresConversion,
) {
  const measurementPreference = useRootSelector(
    (state) => state.userPreferences.measurement,
  )
  return React.useCallback(
    (rawValue: number, target: ConversionTarget) =>
      convertSensorValue({
        fieldName,
        measurementPreference,
        rawValue,
        target,
      }),
    [fieldName, measurementPreference],
  )
}
